import { useCallback, useEffect, useRef, useState } from 'react';
import type {
  ApiService,
  GenerationFeedbackPromptEvent,
  GenerationFeedbackRecord,
  GenerationFeedbackStatus
} from '~/data/restApi';

const DISMISSED_KEY_PREFIX = 'courseGenerationFeedback:dismissed:';
const LEGACY_RATING_SCALE = 5;

interface Options {
  apiService: ApiService | null;
  trainingId: number | string | null | undefined;
  sequenceId: number | null | undefined;
  enabled: boolean;
}

export interface GenerationFeedbackSubmission {
  rating: number;
  comment?: string | null;
}

const isStatus = (value: unknown): value is GenerationFeedbackStatus =>
  typeof value === 'object' &&
  value !== null &&
  !('error' in value) &&
  'eligible' in value;

const dismissedKey = (generationKey: string) =>
  `${DISMISSED_KEY_PREFIX}${generationKey}`;

const generationKeyOf = (status: GenerationFeedbackStatus | null) => {
  const generation = status?.generation;
  if (generation?.id) return `generation:${generation.id}`;
  if (generation?.publicationReceiptId)
    return String(generation.publicationReceiptId);
  return null;
};

const readDismissed = (generationKey: string) => {
  try {
    return window.localStorage.getItem(dismissedKey(generationKey)) === 'true';
  } catch {
    return false;
  }
};

const writeDismissed = (generationKey: string) => {
  try {
    window.localStorage.setItem(dismissedKey(generationKey), 'true');
  } catch {
    return;
  }
};

export const useGenerationFeedbackPrompt = ({
  apiService,
  trainingId,
  sequenceId,
  enabled
}: Options) => {
  const [status, setStatus] = useState<GenerationFeedbackStatus | null>(null);
  const [distinctSlidesViewed, setDistinctSlidesViewed] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const autoPrompted = useRef(false);
  const submitting = useRef(false);
  const openedSlideIds = useRef<Set<number>>(new Set());
  const recordedSlideIds = useRef<Set<number>>(new Set());
  const courseKey = trainingId ? String(trainingId) : null;
  const currentCourseKey = useRef(courseKey);
  currentCourseKey.current = courseKey;

  const raiseSlideCount = (count: number) =>
    setDistinctSlidesViewed(previous => Math.max(previous, count));

  useEffect(() => {
    setStatus(null);
    setDistinctSlidesViewed(0);
    setIsOpen(false);
    autoPrompted.current = false;
    openedSlideIds.current = new Set();
    recordedSlideIds.current = new Set();
  }, [courseKey]);

  useEffect(() => {
    if (sequenceId) openedSlideIds.current.add(sequenceId);
  }, [sequenceId, courseKey]);

  useEffect(() => {
    if (!enabled || !apiService || !courseKey) return;

    let cancelled = false;
    apiService.getGenerationFeedback(courseKey).then(result => {
      if (cancelled || !isStatus(result)) return;

      setStatus(result);
      raiseSlideCount(result.distinctSlidesViewed ?? 0);
    });

    return () => {
      cancelled = true;
    };
  }, [enabled, apiService, courseKey]);

  const generationKey = generationKeyOf(status);
  const eligible = Boolean(status?.eligible && generationKey);
  const latestFeedback: GenerationFeedbackRecord | null =
    status?.latestFeedback ?? null;
  const promptAfterDistinctSlides = status?.promptAfterDistinctSlides ?? 0;
  const ratingScale = status?.ratingScale ?? LEGACY_RATING_SCALE;

  useEffect(() => {
    if (!eligible || !apiService || !courseKey) return;

    openedSlideIds.current.forEach(openedSlideId => {
      if (recordedSlideIds.current.has(openedSlideId)) return;

      recordedSlideIds.current.add(openedSlideId);
      apiService
        .recordGenerationSlideView(courseKey, openedSlideId)
        .then(result => {
          if (currentCourseKey.current !== courseKey) return;
          if ('distinctSlidesViewed' in result) {
            raiseSlideCount(result.distinctSlidesViewed);
          } else {
            recordedSlideIds.current.delete(openedSlideId);
          }
        });
    });
  }, [eligible, apiService, courseKey, sequenceId]);

  const reportPromptEvent = useCallback(
    (promptEvent: GenerationFeedbackPromptEvent) => {
      if (apiService && courseKey) {
        apiService.recordGenerationFeedbackPromptEvent(courseKey, promptEvent);
      }
    },
    [apiService, courseKey]
  );

  useEffect(() => {
    if (!eligible || generationKey === null || autoPrompted.current) return;
    if (latestFeedback || readDismissed(generationKey)) return;
    if (distinctSlidesViewed < promptAfterDistinctSlides) return;

    autoPrompted.current = true;
    setIsOpen(true);
    reportPromptEvent({ event: 'shown', trigger: 'auto' });
  }, [
    eligible,
    generationKey,
    latestFeedback,
    distinctSlidesViewed,
    promptAfterDistinctSlides,
    reportPromptEvent
  ]);

  const open = useCallback(() => {
    if (!eligible) return;

    setIsOpen(true);
    reportPromptEvent({ event: 'shown', trigger: 'manual' });
  }, [eligible, reportPromptEvent]);

  const dismiss = useCallback(() => {
    if (generationKey !== null) writeDismissed(generationKey);
    setIsOpen(false);
    reportPromptEvent({ event: 'dismissed' });
  }, [generationKey, reportPromptEvent]);

  const submit = useCallback(
    async ({ rating, comment }: GenerationFeedbackSubmission) => {
      if (!apiService || !trainingId || !eligible || submitting.current) {
        return false;
      }

      submitting.current = true;
      setIsSubmitting(true);
      try {
        const result = await apiService.submitGenerationFeedback(trainingId, {
          rating,
          ratingScale,
          comment: comment?.trim() || null
        });
        if (!isStatus(result)) return false;

        setStatus(result);
        setIsOpen(false);
        return true;
      } finally {
        submitting.current = false;
        setIsSubmitting(false);
      }
    },
    [apiService, trainingId, eligible, ratingScale]
  );

  return {
    eligible,
    isOpen,
    isSubmitting,
    latestFeedback,
    ratingScale,
    distinctSlidesViewed,
    promptAfterDistinctSlides,
    open,
    dismiss,
    submit
  };
};
